import { Button } from "@dmca/frontend/components/button";
import { Input, Select } from "@dmca/frontend/components/input";
import { ConfirmationModal, Modal } from "@dmca/frontend/components/modal";
import { GlobalNavbarCenter, PageHeader } from "@dmca/frontend/components/nav";
import { Table, useTable } from "@dmca/frontend/components/table";
import {
  checkPermissionsInLoader,
  useAppPermissions,
} from "@dmca/frontend/hooks";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import type { IProductionStorage } from "@dmca/models/interface/production-storage";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbOnIcon from "@mui/icons-material/DoDisturbOn";
import {
  Box,
  Checkbox,
  DialogActions,
  FormControlLabel,
  Grid,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  boolean,
  number,
  object,
  optional,
  string,
  type InferOutput,
} from "valibot";

export const Route = createFileRoute("/_authed/production-storage")({
  component: ProductionStoragePage,
  loader: async ({ context }) =>
    await checkPermissionsInLoader("ProductionJob::ViewJobStorage", context),
  head: () => ({
    meta: [{ title: "Storage Tracker | DNA Dashboard" }],
  }),
});

const TableSchema = object({
  jobId: string(),
  storageLocationId: string(),
  lane: number(),
  cart: optional(number()),
  onSkid: boolean(),
  isRemoved: boolean(),
  isFinished: boolean(),
  notes: optional(string()),
});
type TableSchema = InferOutput<typeof TableSchema>;

type FormValues = {
  jobId: string;
  lane: string;
  cart: string;
  onSkid: boolean;
  isRemoved: boolean;
  isFinished: boolean;
  notes: string;
};

function ProductionStoragePage() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const hasPermission = useAppPermissions();
  const canCreateStorage = hasPermission("ProductionJob::CreateJobStorage");
  const canUpdateStorage = hasPermission("ProductionJob::UpdateJobStorage");
  const canRemoveStorage = hasPermission("ProductionJob::RemoveJobStorage");
  const canDeleteStorage = hasPermission("ProductionJob::DeleteJobStorage");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedPlantId, setSelectedPlantId] = useState("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [editRow, setEditRow] = useState<IProductionStorage | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const { data: locations = [] } = useQuery(
    trpc.cell.getAll.queryOptions({
      byFeatureFlag: CellFeaturesEnum.JOB_STORAGE,
    })
  );

  const plantOptions = useMemo(
    () =>
      Array.from(
        new Map(
          locations.map((location) => [
            location.plantId,
            {
              name: location.plantName,
              value: location.plantId,
            },
          ])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [locations]
  );
  const plantId = selectedPlantId || plantOptions[0]?.value || "";
  const filteredLocations = useMemo(
    () =>
      locations
        .filter((location) => !plantId || location.plantId === plantId)
        .sort((a, b) => a.locationName.localeCompare(b.locationName)),
    [locations, plantId]
  );

  const selectedLocation =
    filteredLocations.find(
      (location) => location.cellId === selectedLocationId
    ) ?? filteredLocations[0];
  const storageLocationId = selectedLocation?.cellId ?? "";

  const { data: storage = [], isLoading } = useQuery(
    trpc.productionStorage.getAll.queryOptions(
      {
        storageLocationId,
        includeRemoved: showRemoved,
      },
      { enabled: !!storageLocationId }
    )
  );

  const createMutation = useMutation(
    trpc.productionStorage.create.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries(trpc.productionStorage.getAll.pathFilter());
        await qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.pathFilter()
        );
        toast.success("Storage record saved.");
        setModalOpen(false);
        setEditRow(undefined);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    })
  );

  const updateMutation = useMutation(
    trpc.productionStorage.update.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries(trpc.productionStorage.getAll.pathFilter());
        await qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.pathFilter()
        );
        toast.success("Storage record saved.");
        setModalOpen(false);
        setEditRow(undefined);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    })
  );

  const removeMutation = useMutation(
    trpc.productionStorage.remove.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries(trpc.productionStorage.getAll.pathFilter());
        await qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.pathFilter()
        );
        toast.success("Storage record removed.");
        setModalOpen(false);
        setEditRow(undefined);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    })
  );
  const deleteMutation = useMutation(
    trpc.productionStorage.delete.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries(trpc.productionStorage.getAll.pathFilter());
        await qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.pathFilter()
        );
        toast.success("Storage record deleted.");
        setModalOpen(false);
        setEditRow(undefined);
        setDeleteConfirmationOpen(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    })
  );

  const tableData = useMemo<TableSchema[]>(
    () =>
      storage.map((row) => ({
        jobId: row.jobId,
        storageLocationId: row.storageLocationId,
        lane: row.lane,
        cart: row.cart,
        onSkid: row.onSkid,
        isRemoved: row.isRemoved,
        isFinished: row.isFinished,
        notes: row.notes,
      })),
    [storage]
  );

  const { getReactTable } = useTable({
    data: tableData,
    schema: TableSchema,
    isLoading,
    cols: {
      jobId: { name: "Work Order" },
      storageLocationId: { show: false },
      lane: { name: "Lane" },
      cart: { name: "Cart" },
      onSkid: {
        name: "On Skid",
        renderFunc: (value) =>
          value ? <CheckCircleIcon color="success" /> : <DoDisturbOnIcon />,
      },
      isRemoved: {
        name: "Removed",
        show: showRemoved,
        renderFunc: (value) =>
          value ? <CheckCircleIcon color="error" /> : <DoDisturbOnIcon />,
      },
      isFinished: {
        name: "Finished",
        show: selectedLocation?.informsProjectStorageStatus ?? false,
        renderFunc: (value) =>
          value ? <CheckCircleIcon color="success" /> : <DoDisturbOnIcon />,
      },
    },
  });

  const handleSubmit = async (data: FormValues) => {
    if (!storageLocationId) return;
    const input = {
      jobId: data.jobId,
      storageLocationId,
      lane: Number(data.lane),
      cart: data.cart ? Number(data.cart) : null,
      onSkid: data.onSkid,
      isRemoved: data.isRemoved,
      isFinished: data.isFinished,
      notes: data.notes || null,
    };

    if (editRow && data.isRemoved !== editRow.isRemoved) {
      try {
        await removeMutation.mutateAsync({
          jobId: input.jobId,
          storageLocationId: input.storageLocationId,
          isRemoved: data.isRemoved,
        });
      } catch {
        //Mutation callbacks display the error
      }
    } else if (data.isRemoved) {
      try {
        await createMutation.mutateAsync(input);
        await removeMutation.mutateAsync({
          jobId: input.jobId,
          storageLocationId: input.storageLocationId,
          isRemoved: true,
        });
      } catch {
        //Mutation callbacks display the error
      }
    } else if (editRow) {
      updateMutation.mutate(input);
    } else {
      createMutation.mutate(input);
    }
  };

  return (
    <>
      <GlobalNavbarCenter>
        <Tabs
          value="tracker"
          onChange={(_, value: "tracker" | "lookup") => {
            if (value === "lookup") {
              navigate({ to: "/production-storage-lookup" });
            }
          }}
          sx={{
            height: "100%",
            "& .MuiTabs-flexContainer": { height: "100%" },
          }}
          centered
        >
          <Tab label="Tracker" value="tracker" />
          <Tab label="Lookup" value="lookup" />
        </Tabs>
      </GlobalNavbarCenter>
      <PageHeader>Storage Tracker</PageHeader>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          {canCreateStorage && (
            <Button
              sx={{
                minWidth: { md: 170 },
                width: { xs: "100%", md: "auto" },
                flexShrink: 0,
              }}
              disabled={!storageLocationId}
              onClick={() => {
                setEditRow(undefined);
                setModalOpen(true);
              }}
            >
              Add Work Order
            </Button>
          )}
          <Select
            label="Plant"
            allowNull={false}
            value={plantId}
            options={plantOptions}
            fullWidth={false}
            sx={{ width: { xs: "100%", md: 160 }, flexShrink: 0 }}
            onChange={(e) => {
              setSelectedPlantId(e.target.value);
              setSelectedLocationId("");
            }}
          />
          <Select
            label="Storage Location"
            allowNull={false}
            value={storageLocationId}
            options={filteredLocations.map((location) => ({
              name: location.locationName,
              value: location.cellId,
            }))}
            fullWidth={false}
            sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0 }}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          />
        </Stack>
        <Table
          getTable={getReactTable}
          onRowClick={
            canUpdateStorage || canRemoveStorage || canDeleteStorage
              ? (row) => {
                  const original = storage.find(
                    (item) =>
                      item.jobId === row.jobId &&
                      item.storageLocationId === row.storageLocationId
                  );
                  if (!original) return;
                  setEditRow(original);
                  setModalOpen(true);
                }
              : undefined
          }
          toolbarContent={
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRemoved}
                  onChange={(e) => setShowRemoved(e.target.checked)}
                />
              }
              label="Show Removed"
            />
          }
          toolbarOptions={{ clearSearchOnFocus: true }}
        />
      </Stack>
      {(canCreateStorage ||
        canUpdateStorage ||
        canRemoveStorage ||
        canDeleteStorage) && (
        <>
          <StorageModal
            open={modalOpen}
            editRow={editRow}
            showFinished={
              selectedLocation?.informsProjectStorageStatus ?? false
            }
            canSave={editRow ? canUpdateStorage : canCreateStorage}
            canRemove={canRemoveStorage}
            canDelete={canDeleteStorage}
            isPending={
              createMutation.isPending ||
              updateMutation.isPending ||
              removeMutation.isPending ||
              deleteMutation.isPending
            }
            onClose={() => {
              setModalOpen(false);
              setEditRow(undefined);
            }}
            onSubmit={handleSubmit}
            onDelete={() => setDeleteConfirmationOpen(true)}
          />
          <ConfirmationModal
            open={deleteConfirmationOpen}
            onClose={() => setDeleteConfirmationOpen(false)}
            onConfirm={() => {
              if (!editRow) return;
              deleteMutation.mutate([
                {
                  jobId: editRow.jobId,
                  storageLocationId: editRow.storageLocationId,
                },
              ]);
            }}
          />
        </>
      )}
    </>
  );
}

function StorageModal({
  open,
  editRow,
  showFinished,
  canSave,
  canRemove,
  canDelete,
  isPending,
  onClose,
  onSubmit,
  onDelete,
}: {
  open: boolean;
  editRow?: IProductionStorage;
  showFinished: boolean;
  canSave: boolean;
  canRemove: boolean;
  canDelete: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
  onDelete: () => void;
}) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(editRow),
    mode: "onChange",
  });

  useEffect(() => {
    if (open) reset(getDefaultValues(editRow));
  }, [editRow, open, reset]);

  const close = () => {
    reset(getDefaultValues());
    onClose();
  };
  const isRemoved = watch("isRemoved");

  return (
    <Modal
      open={open}
      onClose={close}
      header={`${editRow ? "Update" : "Add"} Work Order`}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            control={control}
            name="jobId"
            rules={{
              required: "Work Order ID is required",
              pattern: {
                value: /^\d{7}$/,
                message: "Work Order ID must be a 7 digit number.",
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label="Work Order ID"
                error={!!errors.jobId}
                errorText={errors.jobId?.message}
                disabled={!!editRow || !canSave}
                required
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Controller
            control={control}
            name="lane"
            rules={{ required: "Lane is required." }}
            render={({ field }) => (
              <Input
                {...field}
                label="Lane"
                type="number"
                required
                disabled={!canSave}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <Controller
            control={control}
            name="cart"
            render={({ field }) => (
              <Input
                {...field}
                label="Cart"
                type="number"
                disabled={!canSave}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Input {...field} label="Notes" disabled={!canSave} />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Stack direction="row" flexWrap="wrap">
            <Controller
              control={control}
              name="onSkid"
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      disabled={!canSave}
                    />
                  }
                  label="On Skid"
                />
              )}
            />
            {editRow && canRemove && (
              <Controller
                control={control}
                name="isRemoved"
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label="Is Removed"
                  />
                )}
              />
            )}
            {showFinished && (
              <Controller
                control={control}
                name="isFinished"
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        disabled={!canSave}
                      />
                    }
                    label="Finished"
                  />
                )}
              />
            )}
          </Stack>
        </Grid>
      </Grid>
      <DialogActions>
        {editRow && canDelete && (
          <Button color="error" onClick={onDelete} disabled={isPending}>
            Delete
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={close} variant="outlined" disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          loading={isPending}
          disabled={
            !isValid ||
            (!canSave &&
              !(canRemove && !!editRow && isRemoved !== editRow.isRemoved))
          }
        >
          Save
        </Button>
      </DialogActions>
    </Modal>
  );
}

function getDefaultValues(row?: IProductionStorage): FormValues {
  return {
    jobId: row?.jobId ?? "",
    lane: row?.lane?.toString() ?? "",
    cart: row?.cart?.toString() ?? "",
    onSkid: row?.onSkid ?? false,
    isRemoved: row?.isRemoved ?? false,
    isFinished: row?.isFinished ?? false,
    notes: row?.notes ?? "",
  };
}
